/**
 * 对局会话：数据侧唯一权威。接收输入意图 → 改 BoardState → 发事件。
 * 不碰 DOM，渲染层订阅事件自行更新。
 *
 * 交互规则（与 App 端 InteractionRules 语义一致）：
 *   单击：空 → 白叉；白叉 → 空；星/红叉锁定不动
 *   双击：对照唯一解 —— 命中落星，未命中落红叉并扣一条命
 *   滑动：按起手格决定目标态（空↔白叉），沿途统一涂成该态
 *
 * 「双击对答案」是 StarDoku 的休闲化设计：玩家立刻知道对错，
 * 不必自己验证，代价是每次猜错扣命。3 条命归零即本关失败。
 */

import { EMPTY, STAR, WHITE_CROSS, RED_CROSS, isRemovable, countsAsStar } from './cellState.js';
import { BoardState } from './boardState.js';
import { nextHint } from './hintEngine.js';

/** 极简事件发射器。 */
class Emitter {
    constructor() {
        /** @type {Map<string, Set<Function>>} */
        this._handlers = new Map();
    }

    /** @param {string} event @param {Function} fn @returns {() => void} 取消订阅 */
    on(event, fn) {
        let set = this._handlers.get(event);
        if (!set) this._handlers.set(event, (set = new Set()));
        set.add(fn);
        return () => set.delete(fn);
    }

    /** @param {string} event @param {...any} args */
    emit(event, ...args) {
        const set = this._handlers.get(event);
        if (!set) return;
        for (const fn of set) fn(...args);
    }
}

export const DEFAULT_MAX_LIVES = 3;

export class GameSession extends Emitter {
    /**
     * @param {object} opts
     * @param {number} opts.dim
     * @param {number[][]} opts.regionGrid
     * @param {number} opts.stars
     * @param {number[]} opts.solution 唯一解，solution[r] = 该行星星所在列
     * @param {number} [opts.maxLives]
     */
    constructor({ dim, regionGrid, stars, solution, maxLives = DEFAULT_MAX_LIVES }) {
        super();
        this.board = new BoardState(dim, regionGrid, stars);
        this.solution = solution;
        this.maxLives = maxLives;
        this.remainingLives = maxLives;
        this.isComplete = false;
        this.isFailed = false;

        /** 本关操作流，用于存档与撤销。 */
        this.history = [];
        /** 当前滑动涂画的目标态；null = 未在涂画。 */
        this._paintTarget = null;
    }

    get dim() {
        return this.board.dim;
    }

    /** 本关已失误次数。 */
    get mistakes() {
        return this.maxLives - this.remainingLives;
    }

    /** 对局是否已结束（通关或失败）。 */
    get isOver() {
        return this.isComplete || this.isFailed;
    }

    /** 该格在唯一解里是否为星。 */
    isSolutionStar(r, c) {
        return this.solution[r] === c;
    }

    /**
     * 假设在 (r,c) 放一颗星，会违反哪几条规则。
     *
     * 注意：放错**不等于**违反规则 —— 一个位置可能局部完全合法，只是与唯一解不符
     * （纯推理失误）。这种情况返回空数组，UI 就不该指着某条规则说玩家违反了它。
     *
     * @param {number} r @param {number} c
     * @returns {Array<'rowcol' | 'region' | 'adjacent'>}
     */
    violatedRules(r, c) {
        const b = this.board;
        const need = b.stars;
        const out = [];

        if (b.starsInRow[r] >= need || b.starsInCol[c] >= need) out.push('rowcol');
        if (b.starsInRegion[b.regionAt(r, c)] >= need) out.push('region');

        for (let dr = -1; dr <= 1 && !out.includes('adjacent'); dr++) {
            for (let dc = -1; dc <= 1; dc++) {
                if (dr === 0 && dc === 0) continue;
                const nr = r + dr, nc = c + dc;
                if (!b.inBounds(nr, nc)) continue;
                if (countsAsStar(b.get(nr, nc))) { out.push('adjacent'); break; }
            }
        }
        return out;
    }

    /**
     * 单击/双击的状态转移。返回目标态，null 表示无效操作。
     * @param {number} current @param {'single'|'double'} gesture
     * @param {number} r @param {number} c
     * @returns {number | null}
     */
    _resolve(current, gesture, r, c) {
        if (gesture === 'single') {
            if (current === EMPTY) return WHITE_CROSS;
            if (isRemovable(current)) return EMPTY; // 白叉 → 空
            return null;                             // 星/红叉锁定
        }
        // double
        if (!isRemovable(current)) return null;      // 星/红叉锁定
        return this.isSolutionStar(r, c) ? STAR : RED_CROSS;
    }

    /**
     * 施加一次手势。
     * @param {number} r @param {number} c
     * @param {'single'|'double'} gesture
     * @returns {boolean} 是否真的改变了棋盘
     */
    applyGesture(r, c, gesture) {
        if (this.isOver || !this.board.inBounds(r, c)) return false;

        const current = this.board.get(r, c);
        const next = this._resolve(current, gesture, r, c);
        if (next === null || next === current) return false;

        // 违规判定要在写盘之前做，否则刚落的这颗会把自己算进去
        const violated = next === RED_CROSS ? this.violatedRules(r, c) : null;

        this._commit(r, c, current, next);
        if (violated && violated.length) this.emit('ruleViolated', violated);
        return true;
    }

    /**
     * 写入一格并派生事件、判定生命与胜负。
     * @param {number} r @param {number} c @param {number} from @param {number} to
     */
    _commit(r, c, from, to) {
        this.board.set(r, c, to);
        this.history.push({ r, c, from, to });
        this.emit('cellChanged', { r, c, from, to });

        if (to === RED_CROSS) {
            this.remainingLives = Math.max(0, this.remainingLives - 1);
            this.emit('lifeLost', this.remainingLives);
            this.emit('livesChanged', this.remainingLives);
            if (this.remainingLives === 0) {
                this.isFailed = true;
                this.emit('failed');
                return;
            }
        }

        if (this.board.isComplete()) {
            this.isComplete = true;
            this.emit('completed');
        }
    }

    // ── 滑动涂画 ──

    /**
     * 起手：按该格当前态决定本次涂画的目标态。
     * 只在「空 ↔ 白叉」之间涂，起手落在锁定格上则本次不涂画。
     * @param {number} r @param {number} c
     */
    beginPaint(r, c) {
        if (this.isOver || !this.board.inBounds(r, c)) return;

        const current = this.board.get(r, c);
        if (!isRemovable(current)) {
            this._paintTarget = null;
            return;
        }
        this._paintTarget = current === EMPTY ? WHITE_CROSS : EMPTY;
        this.paintAt(r, c);
    }

    /**
     * 涂到某格。目标态相同或该格锁定则跳过。
     * @param {number} r @param {number} c
     */
    paintAt(r, c) {
        if (this._paintTarget === null) return;
        if (this.isOver || !this.board.inBounds(r, c)) return;

        const current = this.board.get(r, c);
        if (!isRemovable(current)) return;
        if (current === this._paintTarget) return;

        this._commit(r, c, current, this._paintTarget);
    }

    endPaint() {
        this._paintTarget = null;
    }

    /** 是否正在涂画（输入层用来决定 move 事件要不要处理）。 */
    get isPainting() {
        return this._paintTarget !== null;
    }

    // ── 道具与重试 ──

    /**
     * 橡皮：清掉所有白叉，保留星与红叉（锁定态不可擦）。
     * @returns {Array<{r:number,c:number}>} 被清掉的格子
     */
    eraseMarks() {
        if (this.isOver) return [];
        const cleared = [];
        for (let r = 0; r < this.dim; r++)
            for (let c = 0; c < this.dim; c++) {
                if (this.board.get(r, c) !== WHITE_CROSS) continue;
                this._commit(r, c, WHITE_CROSS, EMPTY);
                cleared.push({ r, c });
            }
        return cleared;
    }

    /**
     * 教学式提示：求当前盘面"最简单的下一步"（不落子，展示与应用由 UI 层负责）。
     * @returns {import('./hintEngine.js').Hint | null}
     */
    nextHint() {
        return nextHint(this);
    }

    /**
     * 应用提示的一格结论。只允许提示引擎产出的三种安全转移：
     * 清错叉（白叉→空）、排除（空→白叉）、落星（空→星，且必须在解上）。
     * 不扣命（提示是教学，不是惩罚）。
     * @param {import('./hintEngine.js').HintAction} a
     * @returns {boolean} 是否真的改变了棋盘
     */
    applyHintAction({ r, c, to }) {
        if (this.isOver || !this.board.inBounds(r, c)) return false;
        const cur = this.board.get(r, c);

        const legal =
            (to === EMPTY && cur === WHITE_CROSS) ||
            (to === WHITE_CROSS && cur === EMPTY) ||
            (to === STAR && cur === EMPTY && this.isSolutionStar(r, c));
        if (!legal) return false;

        this._commit(r, c, cur, to);
        return true;
    }

    /** 重置本关：清盘、满命、清历史。 */
    reset() {
        this.board.clear();
        this.remainingLives = this.maxLives;
        this.isComplete = false;
        this.isFailed = false;
        this.history.length = 0;
        this._paintTarget = null;
        this.emit('livesChanged', this.remainingLives);
        this.emit('reset');
    }
}
